import type { Events } from '@george-web/event-factory';

import { logger } from '@luna/logger';

import { getTackerRouterOptions } from './trackerRouterOptionsSingleton';
import { TrackingEvent } from './model/TrackingEvent';
import type { BaseTrackEvent } from './model/trackingTypes';
import { trackEventWithData } from './StatisticsTrackWithDataService';
import { LuxTracker } from './trackers/LuxTracker';
import { getPosthogTracker } from './PosthogService';
import { getBeanCounterTracker } from './BeancounterService';
import type { PostHogTracker } from './trackers/PostHogTracker';

const beanCounterTracker = getBeanCounterTracker();
const luxTracker = new LuxTracker();
const postHogTracker = getPosthogTracker();

export function getTrackers(): {
    luxTracker: LuxTracker;
    postHogTracker: PostHogTracker;
} {
    return {
        luxTracker,
        postHogTracker,
    };
}

export function trackPage(normalizedRoute?: string, payload?: Record<string, any>): void {
    const { getNormalizedRoute } = getTackerRouterOptions();
    const route = normalizedRoute || getNormalizedRoute();

    beanCounterTracker.track(new TrackingEvent('page', 'pageChanged', route, payload));
    postHogTracker.track(new TrackingEvent('page', '$pageview', route, payload));
}

export type FlatEventData = {
    normalized: string;
    eventValue1: string;
} & BaseTrackEvent;

function getEventData(category: string, normalizedURL: string, optValue: unknown) {
    return {
        category,
        normalized: normalizedURL,
        eventValue1: typeof optValue === 'string' ? optValue : JSON.stringify(optValue),
    };
}

/**
 * Category is set in the event's "data" property,
 * Name is the event's name,
 * Optional values are set in the event's "data" property.
 * @deprecated Use trackEvent2 instead
 */
export function trackEvent(category: string, name: string, optValue?: object): void {
    if (typeof optValue === 'object') {
        trackEventWithData(category, name, optValue);
        return;
    }

    const { getRoute, getNormalizedRoute } = getTackerRouterOptions();

    const trackingEvent = new TrackingEvent<FlatEventData>(
        category,
        name,
        getRoute(),
        getEventData(category, getNormalizedRoute(), optValue),
    );

    beanCounterTracker
        .track(trackingEvent)
        .catch((e) => logger.warn('could not track beanCounter event', e));
    postHogTracker
        .track(trackingEvent)
        .catch((e) => logger.warn('could not track posthog event', e));
}

/**
 * Type-safe trackEvent function which uses the event-factory generated types.
 * */
export function trackEvent2<Category extends keyof Events, Name extends keyof Events[Category]>(
    category: Category,
    name: Name,
    optValue?: Events[Category][Name] extends void ? void : Events[Category][Name],
): void {
    trackEvent(category, name as string, optValue as object);
}

function externalLinkTracing(target: HTMLElement, category: string) {
    if (target.getAttribute('target') === '_blank') {
        trackEvent(category, 'externalLinkClicked', { target: target.getAttribute('href') ?? '/' });
    }
}

export async function flushTrackingQueue(): Promise<void> {
    await Promise.all([
        beanCounterTracker.flushQueue(),
        luxTracker.flushQueue(),
        postHogTracker.flushQueue(),
    ]);
}

export function stopTracking(): void {
    beanCounterTracker.stop();
    postHogTracker.stop();
    luxTracker.stop();
}

export const georgeStats = {
    externalLinkTracing,
    trackEvent,
};
